import React, { useEffect, useState } from "react";
import { X, ClipboardList, Globe } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import ApiService from "../../services/Api.service";

const VoiceCharacter = ({
  isOpen,
  onClose,
  onSave,
  itemData,
}) => {
  const [languageMode, setLanguageMode] = useState("default");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [languageOptions, setLanguageOptions] = useState({});
  const [ttsOptions, setTtsOptions] = useState([]);
  const [voiceSettings, setVoiceSettings] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      tts_type: "",
      voice_id: "",
      speech_text: "नमस्ते! मैं आपका virtual assistant बोल रहा हूँ / रही हूँ । मैं आपकी मदद के लिए यहाँ हूँ। Please बताइए, मैं कैसे help कर सकता हूँ / सकती हूँ?",
      language_mode: "default",
      language: "",
      gender: "female",
      is_active: true,
    },
  });

  const fetchLanguageOptions = async () => {
    try {
      const languageRes = await ApiService.getLanguageOptions();
      if (languageRes.status === 200) {
        setLanguageOptions(languageRes.data);

        const defaultLang =
          Array.isArray(languageRes.data?.default_language) && languageRes.data.default_language.length > 0
            ? languageRes.data.default_language[0].value
            : "";
        setSelectedLanguage(defaultLang);
      }
    } catch (error) {
      console.error("Error fetching language options:", error);
    }
  };

  const fetchTtsOptions = async (selectedLanguage) => {
    try {
      const payload = {
        language: selectedLanguage
      }
      const ivrOptionsRes = await ApiService.getIvrOptions(payload);

      if (ivrOptionsRes.status === 200) {
        const options = ivrOptionsRes.data.tts_options || [];
        setTtsOptions(options);
        if (!itemData && options.length > 0 && !getValues("tts_type")) {
          setValue("tts_type", options[0].value);
        }
      }
    } catch (error) {
      console.error("Error fetching TTS options:", error);
    }
  };

  useEffect(() => {
    fetchLanguageOptions();
  }, []);

  useEffect(() => {
    if (selectedLanguage) {
      fetchTtsOptions(selectedLanguage);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (itemData) {
      reset({
        name: itemData.name || "",
        description: itemData.description || "",
        tts_type: itemData.tts_type || "",
        voice_id: itemData.voice_id || "",
        speech_text: itemData.speech_text || "नमस्ते! मैं आपका virtual assistant बोल रहा हूँ / रही हूँ । मैं आपकी मदद के लिए यहाँ हूँ। Please बताइए, मैं कैसे help कर सकता हूँ / सकती हूँ?",
        language:
          itemData.language && itemData.language !== languageOptions.default_language?.[0]?.value
            ? itemData.language
            : languageOptions.default_language?.[0]?.value || "",
        language_mode:
          itemData.language && itemData.language !== languageOptions.default_language?.[0]?.value
            ? "custom"
            : "default",
        gender: itemData.gender || "female",
        is_active: itemData.is_active !== false,
      });
      setVoiceSettings(itemData.voice_settings || {});
      setShowAdvanced(false);
    } else {
      reset({
        name: "",
        description: "",
        tts_type: "",
        voice_id: "",
        speech_text: "नमस्ते! मैं आपका virtual assistant बोल रहा हूँ / रही हूँ । मैं आपकी मदद के लिए यहाँ हूँ। Please बताइए, मैं कैसे help कर सकता हूँ / सकती हूँ?",
        language_mode: "default",
        language: languageOptions.default_language?.[0]?.value || "",
        gender: "female",
        is_active: true,
      });
      setVoiceSettings({});
      setShowAdvanced(false);
    }
  }, [itemData, reset, languageOptions]);

  const selectedTtsType = watch("tts_type");

  useEffect(() => {
    if (!selectedTtsType) {
      setVoiceSettings({});
      return;
    }

    if (itemData && selectedTtsType === itemData.tts_type && itemData.voice_settings && Object.keys(itemData.voice_settings).length > 0) {
      setVoiceSettings(itemData.voice_settings);
      return;
    }

    const fetchDefaultSettings = async () => {
      try {
        const res = await ApiService.getDefaultVoiceSetting(selectedTtsType);
        
        if (res.status === 200 && res.data) {
          if (res.data && !res.data.message) {
            setVoiceSettings(res.data);
          } else {
            setVoiceSettings({});
          }
        }
      } catch (error) {
        console.error("Error fetching default voice settings:", error);
        setVoiceSettings({});
      }
    };

    fetchDefaultSettings();
  }, [selectedTtsType, itemData]);

  const handleSettingChange = (key, val) => {
    setVoiceSettings((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const onSubmit = (data) => {
    const payload = {
      name: data.name.trim(),
      description: data.description.trim(),
      tts_type: data.tts_type,
      voice_id: data.voice_id.trim(),
      speech_text: data.speech_text.trim(),
      language:
        data.language_mode === "default"
          ? languageOptions?.default_language?.[0]?.value
          : data.language,
      gender: data.gender,
      is_active: data.is_active,
      voice_settings: voiceSettings,
    };

    onSave(payload);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="add-subparam-overlay">
      <div
        className="add-subparam-modal"
        style={{ maxWidth: 650, width: "90%" }}
      >
        {/* Header */}
        <div className="add-subparam-header">
          <h3>
            <ClipboardList size={18} />
            {itemData ? "Edit Voice Character" : "Add Voice Character"}
          </h3>

          <button onClick={onClose} className="close-btn">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form
          id="voiceCharacterForm"
          onSubmit={handleSubmit(onSubmit)}
          className="add-subparam-body"
        >
          {/* Row 1 */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label>Name *</label>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Name is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    className="form-control"
                    placeholder="Voice Character Name"
                  />
                )}
              />
              {errors.name && (
                <p className="error-text">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label>Voice ID *</label>
              <Controller
                name="voice_id"
                control={control}
                rules={{ required: "Voice ID is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    className="form-control"
                    placeholder="hi-IN-SwaraNeural"
                  />
                )}
              />
              {errors.voice_id && (
                <p className="error-text">{errors.voice_id.message}</p>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginTop: 10,
            }}
          >
            <div>
              <label>Gender</label>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <select {...field} className="form-control">
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                )}
              />
            </div>
          </div>

          {/* Language */}
          <div style={{ marginTop: 10 }}>
            <label>
              <Globe size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />
              Language Mode
            </label>

            <Controller
              name="language_mode"
              control={control}
              render={({ field }) => (
                <div className="flex gap-3" style={{ display: "flex", gap: "20px" }}>
                  {/* DEFAULT LANGUAGE */}
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="default"
                      checked={field.value === "default"}
                      onChange={() => {
                        field.onChange("default");
                        setLanguageMode("default");
                        setSelectedLanguage(languageOptions.default_language?.[0]?.value || "");
                      }}
                    />
                    {languageOptions.default_language?.[0]?.label || "English"} (Default)
                  </label>

                  {/* CUSTOM LANGUAGE */}
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="custom"
                      checked={field.value === "custom"}
                      onChange={() => field.onChange("custom")}
                    />
                    Select Other Language
                  </label>
                </div>
              )}
            />

            {/* SHOW DROPDOWN ONLY IF custom selected */}
            {watch("language_mode") === "custom" && (
              <Controller
                name="language"
                control={control}
                rules={{ required: "Please select language" }}
                render={({ field }) => (
                  <select
                    {...field}
                    className="form-control"
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setSelectedLanguage(e.target.value);
                    }}
                  >
                    <option value="">Select Language</option>
                    {languageOptions.other_languages?.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                )}
              />
            )}
          </div>

          {/* TTS Type */}
          <div style={{ marginTop: 10 }}>
            <label>TTS Type *</label>
            <Controller
              name="tts_type"
              control={control}
              render={({ field }) => (
                <select {...field} className="form-control">
                  <option value="">Select TTS</option>
                  {ttsOptions?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
          
          {/* Advanced Settings */}
          <div style={{ marginTop: 15, borderTop: "1px solid var(--gray-200)", paddingTop: 15 }}>
             <button
                type="button"
                className="advanced-toggle-btn"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? "Hide" : "Show"} Advanced Settings
              </button>

            {showAdvanced && (
              <div
                style={{
                  marginTop: 12,
                  padding: "16px",
                  borderRadius: "8px",
                  background: "var(--slate-50)",
                  border: "1px solid var(--slate-200)",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                {Object.keys(voiceSettings).length > 0 ? (
                  Object.entries(voiceSettings).map(([key, value]) => {
                    const isBool = typeof value === "boolean";
                    return (
                      <div key={key} style={{ display: "flex", flexDirection: "column" }}>
                        <label
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "var(--slate-600)",
                            textTransform: "capitalize",
                            marginBottom: "6px",
                          }}
                        >
                          {key.replace(/_/g, " ")}
                        </label>
                        {isBool ? (
                          <select
                            value={value ? "true" : "false"}
                            onChange={(e) => handleSettingChange(key, e.target.value === "true")}
                            className="form-control"
                            style={{ padding: "6px 12px", fontSize: "13px", height: "38px" }}
                          >
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        ) : (
                          <input
                            type={typeof value === "number" ? "number" : "text"}
                            step={typeof value === "number" ? "any" : undefined}
                            value={value ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleSettingChange(
                                key,
                                typeof value === "number" ? (val === "" ? "" : Number(val)) : val
                              );
                            }}
                            className="form-control"
                            style={{ padding: "6px 12px", fontSize: "13px", height: "38px" }}
                          />
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p style={{ gridColumn: "span 2", fontSize: "13px", color: "var(--gray-500)", margin: 0, textAlign: "center" }}>
                    No advanced settings available for this TTS Type.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginTop: 10 }}>
            <label>Description</label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  rows={2}
                  className="form-control"
                  placeholder="Voice Description"
                />
              )}
            />
          </div>

          {/* Preview Text */}
          <div style={{ marginTop: 10 }}>
            <label>Speech text *</label>
            <Controller
              name="speech_text"
              control={control}
              rules={{ required: "Speech text is required" }}
              render={({ field }) => (
                <textarea
                  {...field}
                  rows={3}
                  className="form-control"
                />
              )}
            />
            {errors.speech_text && (
              <p className="error-text">{errors.speech_text.message}</p>
            )}
          </div>

          {/* Active */}
          <div
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) =>
                    field.onChange(e.target.checked)
                  }
                />
              )}
            />
            <label style={{ margin: 0 }}>Active</label>
          </div>

        </form>

        {/* Footer */}
        <div className="add-subparam-footer">
          <button
            type="button"
            className="popup-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="submit"
            form="voiceCharacterForm"
            className="popup-add-btn"
          >
            {itemData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceCharacter;